import React, { useState, useEffect, useRef } from 'react';
import './SpotifyTest.css';

const SpotifyTest: React.FC = () => {
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyUser, setSpotifyUser] = useState<any>(null);
  const [testResults, setTestResults] = useState<string[]>([]);
  const [playlistName, setPlaylistName] = useState('Test Playlist');
  const [playlistDescription, setPlaylistDescription] = useState('A test playlist created via API');
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState<any>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  const addLog = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const handleSpotifyLogin = () => {
    addLog('🔗 Initiating Spotify login (localStorage method)...');
    const returnUrl = encodeURIComponent('/spotify-test');
    window.location.href = `/api/auth/spotify/login?returnUrl=${returnUrl}`;
  };

  const handleSpotifyLoginCookie = () => {
    addLog('🍪 Initiating Spotify login (cookie method)...');
    const returnUrl = encodeURIComponent('/spotify-test');
    window.location.href = `/api/auth/spotify/login-cookie?returnUrl=${returnUrl}`;
  };

  const checkSpotifyConnection = async () => {
    try {
      addLog('🔍 Checking Spotify connection (localStorage method)...');
      const response = await fetch('/api/auth/spotify/status');
      addLog(`📊 Status response: ${response.status} ${response.statusText}`);

      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Spotify connected: ${JSON.stringify(data)}`);
        setSpotifyConnected(data.connected);
        setSpotifyUser(data.user);
        setConnectionDetails(data);
      } else {
        const errorText = await response.text();
        addLog(`❌ Spotify not connected: ${errorText}`);
        setSpotifyConnected(false);
        setSpotifyUser(null);
        setConnectionDetails(null);
      }
    } catch (error) {
      addLog(`❌ Error checking Spotify connection: ${error}`);
      setSpotifyConnected(false);
      setSpotifyUser(null);
      setConnectionDetails(null);
    }
  };

  const checkSpotifyConnectionCookie = async () => {
    try {
      console.log('🍪 Button clicked - checking Spotify connection (cookie method)...');
      addLog('🍪 Checking Spotify connection (cookie method)...');
      addLog('🔗 Making request to /api/auth/spotify/status-cookie');
      
      const response = await fetch('/api/auth/spotify/status-cookie');
      console.log('🍪 Response received:', response.status, response.statusText);
      addLog(`📊 Response status: ${response.status} ${response.statusText}`);
      addLog(`📊 Response headers: ${JSON.stringify(Object.fromEntries(response.headers.entries()))}`);

      if (response.ok) {
        const data = await response.json();
        addLog(`📦 Response data: ${JSON.stringify(data)}`);
        
        // Update UI state if connected
        if (data.connected) {
          addLog('✅ Setting UI state to connected');
          setSpotifyConnected(true);
          setSpotifyUser(data.user);
          setConnectionDetails(data);
        } else {
          addLog('❌ Response indicates not connected');
        }
        
        return data;
      } else {
        const errorText = await response.text();
        addLog(`❌ Response not OK: ${errorText}`);
        return { connected: false, error: errorText };
      }
    } catch (error: any) {
      addLog(`❌ Fetch error: ${error.message}`);
      addLog(`❌ Error stack: ${error.stack}`);
      return { connected: false, error: error.message };
    }
  };

  const compareAuthenticationMethods = async () => {
    addLog('🔬 Comparing authentication methods...');
    
    const localStorageResult: any = await checkSpotifyConnection();
    const cookieResult: any = await checkSpotifyConnectionCookie();
    
    addLog('📊 Comparison Results:');
    addLog(`  localStorage: ${localStorageResult?.connected ? '✅ Connected' : '❌ Not connected'}`);
    addLog(`  Cookie: ${cookieResult?.connected ? '✅ Connected' : '❌ Not connected'}`);
    
    if (localStorageResult?.connected && cookieResult?.connected) {
      addLog('🎉 Both methods are connected!');
    } else if (localStorageResult?.connected) {
      addLog('📝 Only localStorage method is connected');
    } else if (cookieResult?.connected) {
      addLog('🍪 Only cookie method is connected');
    } else {
      addLog('❌ Neither method is connected');
    }
  };

  const testDetailedConnection = async () => {
    setIsTestingConnection(true);
    addLog('🔬 Starting detailed connection test...');
    
    try {
      // Test 1: Check local tokens
      addLog('📋 Test 1: Checking local token storage...');
      const response = await fetch('/api/auth/spotify/status?detailed=true');
      addLog(`📊 Detailed status response: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Local tokens: ${data.hasAccessToken ? '✅ Present' : '❌ Missing'}`);
        addLog(`✅ Refresh token: ${data.hasRefreshToken ? '✅ Present' : '❌ Missing'}`);
        addLog(`⏰ Token expiry: ${data.tokenExpiry || 'Unknown'}`);
        setConnectionDetails(data);
      } else {
        const errorText = await response.text();
        addLog(`❌ Detailed check failed: ${errorText}`);
      }
      
      // Test 2: Test Spotify API call
      addLog('🌐 Test 2: Testing Spotify API call...');
      const apiResponse = await fetch('/api/auth/spotify/test-api');
      addLog(`📊 API test response: ${apiResponse.status}`);
      
      if (apiResponse.ok) {
        const apiData = await apiResponse.json();
        addLog(`✅ API call successful: ${apiData.message}`);
        addLog(`👤 User profile: ${apiData.user?.display_name || 'Unknown'}`);
      } else {
        const apiError = await apiResponse.text();
        addLog(`❌ API call failed: ${apiError}`);
      }
      
      // Test 3: Test token refresh capability
      addLog('🔄 Test 3: Testing token refresh capability...');
      const refreshResponse = await fetch('/api/auth/spotify/test-refresh');
      addLog(`📊 Refresh test response: ${refreshResponse.status}`);
      
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        addLog(`✅ Refresh capability: ${refreshData.canRefresh ? '✅ Available' : '❌ Not available'}`);
        if (refreshData.message) {
          addLog(`📝 Refresh message: ${refreshData.message}`);
        }
      } else {
        const refreshError = await refreshResponse.text();
        addLog(`❌ Refresh test failed: ${refreshError}`);
      }
      
    } catch (error) {
      addLog(`❌ Detailed connection test failed: ${error}`);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const forceTokenRefresh = async () => {
    addLog('🔄 Forcing token refresh...');
    try {
      const response = await fetch('/api/auth/spotify/force-refresh', {
        method: 'POST'
      });
      
      addLog(`📊 Force refresh response: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Token refresh successful: ${data.message}`);
        // Re-check connection after refresh
        setTimeout(checkSpotifyConnection, 1000);
      } else {
        const error = await response.text();
        addLog(`❌ Force refresh failed: ${error}`);
      }
    } catch (error) {
      addLog(`❌ Error during force refresh: ${error}`);
    }
  };

  const simulateExpiredToken = async () => {
    addLog('⏰ Simulating expired token scenario...');
    try {
      // First, check current status
      addLog('📋 Before simulation - checking current status...');
      const beforeResponse = await fetch('/api/auth/spotify/status?detailed=true');
      if (beforeResponse.ok) {
        const beforeData = await beforeResponse.json();
        addLog(`📊 Before: Access token ${beforeData.hasAccessToken ? '✅ Present' : '❌ Missing'}`);
      }
      
      // Simulate expired token
      const response = await fetch('/api/auth/spotify/simulate-expired', {
        method: 'POST'
      });
      
      addLog(`📊 Simulation response: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        addLog(`✅ Simulation completed: ${data.message}`);
        
        // Check status immediately after simulation
        addLog('📋 After simulation - checking status...');
        const afterResponse = await fetch('/api/auth/spotify/status?detailed=true');
        if (afterResponse.ok) {
          const afterData = await afterResponse.json();
          addLog(`📊 After: Access token ${afterData.hasAccessToken ? '✅ Present' : '❌ Missing'}`);
          if (afterData.hasAccessToken) {
            addLog('🔄 Token was automatically refreshed!');
          }
        }
        
        // Re-check connection after simulation
        setTimeout(checkSpotifyConnection, 1000);
      } else {
        const error = await response.text();
        addLog(`❌ Simulation failed: ${error}`);
      }
    } catch (error) {
      addLog(`❌ Error during simulation: ${error}`);
    }
  };

  const testTokenRefreshFlow = async () => {
    addLog('🔄 Testing complete token refresh flow...');
    try {
      // Step 1: Clear access token
      addLog('📋 Step 1: Clearing access token...');
      const clearResponse = await fetch('/api/auth/spotify/simulate-expired', {
        method: 'POST'
      });
      
      if (!clearResponse.ok) {
        addLog('❌ Failed to clear access token');
        return;
      }
      
      // Step 2: Try to use the API (should trigger refresh)
      addLog('📋 Step 2: Testing API call with expired token...');
      const apiResponse = await fetch('/api/auth/spotify/test-api');
      addLog(`📊 API response: ${apiResponse.status}`);
      
      if (apiResponse.ok) {
        const apiData = await apiResponse.json();
        addLog(`✅ API call successful after refresh: ${apiData.message}`);
        addLog(`👤 User: ${apiData.user?.display_name}`);
      } else {
        const apiError = await apiResponse.text();
        addLog(`❌ API call failed: ${apiError}`);
      }
      
      // Step 3: Verify final status
      addLog('📋 Step 3: Verifying final connection status...');
      const statusResponse = await fetch('/api/auth/spotify/status?detailed=true');
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        addLog(`📊 Final status: Connected ${statusData.connected ? '✅' : '❌'}`);
        addLog(`📊 Access token: ${statusData.hasAccessToken ? '✅ Present' : '❌ Missing'}`);
      }
      
    } catch (error) {
      addLog(`❌ Error during refresh flow test: ${error}`);
    }
  };

  const clearLogs = () => {
    setTestResults([]);
  };

  const copyLogs = () => {
    const logsText = testResults.join('\n');
    navigator.clipboard.writeText(logsText).then(() => {
      addLog('📋 Logs copied to clipboard!');
    }).catch(() => {
      addLog('❌ Failed to copy logs');
    });
  };

  const handleLogout = async () => {
    try {
      addLog('🚪 Logging out from Spotify...');
      const response = await fetch('/api/auth/spotify/logout', {
        method: 'POST'
      });
      
      if (response.ok) {
        addLog('✅ Logout successful');
        setSpotifyConnected(false);
        setSpotifyUser(null);
      } else {
        addLog('❌ Logout failed');
      }
    } catch (error) {
      addLog(`❌ Error during logout: ${error}`);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!spotifyConnected || !spotifyUser) {
      addLog('❌ Must be connected to Spotify to create playlist');
      return;
    }

    setIsCreatingPlaylist(true);
    addLog('🎵 Creating test playlist...');

    try {
      // Sample playlist data
      const playlistData = {
        name: playlistName,
        description: playlistDescription,
        songs: [
          { name: 'Bohemian Rhapsody', artist: 'Queen', album: 'A Night at the Opera' },
          { name: 'Hotel California', artist: 'Eagles', album: 'Hotel California' },
          { name: 'Stairway to Heaven', artist: 'Led Zeppelin', album: 'Led Zeppelin IV' },
          { name: 'Imagine', artist: 'John Lennon', album: 'Imagine' },
          { name: 'Hey Jude', artist: 'The Beatles', album: 'The Beatles 1967-1970' }
        ]
      };

      addLog(`📤 Sending request to /api/spotify/create-playlist`);
      addLog(`📦 Request data: ${JSON.stringify(playlistData)}`);
      
      const response = await fetch('/api/spotify/create-playlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(playlistData)
      });
      
      addLog(`📥 Response status: ${response.status} ${response.statusText}`);

      if (response.ok) {
        const result = await response.json();
        addLog(`✅ Playlist created successfully!`);
        addLog(`📝 Playlist ID: ${result.playlist_id}`);
        addLog(`🔗 Playlist URL: ${result.playlist_url}`);
        addLog(`📊 Songs added: ${result.songs_added}/${playlistData.songs.length}`);
      } else {
        const error = await response.text();
        addLog(`❌ Failed to create playlist: ${error}`);
      }
    } catch (error) {
      addLog(`❌ Error creating playlist: ${error}`);
      addLog(`🔍 Error details: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsCreatingPlaylist(false);
    }
  };

  // Check URL parameters on mount (but don't make automatic API calls)
  useEffect(() => {
    addLog('🚀 Component mounted, checking URL parameters...');
    const urlParams = new URLSearchParams(window.location.search);
    addLog(`📝 URL parameters: ${JSON.stringify(Object.fromEntries(urlParams.entries()))}`);

    if (urlParams.get('error')) {
      addLog(`❌ Error parameter found: ${urlParams.get('error')} - ${urlParams.get('message')}`);
    }
  }, []);

  return (
    <div className="spotify-test-container">
      <div className="spotify-test-header">
        <h1>🎵 Spotify Integration Test</h1>
        <p>Test Spotify OAuth and playlist functionality</p>
      </div>

      <div className="spotify-test-controls">
        <div className="test-section">
          <h3>🔗 OAuth Testing (localStorage Method)</h3>
          <button
            className="test-button primary"
            onClick={handleSpotifyLogin}
          >
            {spotifyConnected ? 'Reconnect to Spotify' : 'Connect to Spotify'}
          </button>
          
          <button
            className="test-button secondary"
            onClick={checkSpotifyConnection}
          >
            Check Connection Status
          </button>
          
          {spotifyConnected && (
            <button
              className="test-button danger"
              onClick={handleLogout}
            >
              Disconnect from Spotify
            </button>
          )}
        </div>

        <div className="test-section">
          <h3>🍪 OAuth Testing (Cookie Method)</h3>
          <button
            className="test-button primary"
            onClick={handleSpotifyLoginCookie}
          >
            Connect to Spotify (Cookie)
          </button>
          
          <button
            className="test-button secondary"
            onClick={checkSpotifyConnectionCookie}
          >
            Check Cookie Connection
          </button>
          
          <button
            className="test-button secondary"
            onClick={compareAuthenticationMethods}
          >
            🔬 Compare Both Methods
          </button>
        </div>

        <div className="test-section">
          <h3>📊 Connection Status</h3>
          <div className="status-display">
            <p><strong>Status:</strong> {spotifyConnected ? '✅ Connected' : '❌ Not Connected'}</p>
            {spotifyUser && (
              <p><strong>User:</strong> {spotifyUser.display_name} ({spotifyUser.id})</p>
            )}
            {connectionDetails && (
              <div className="connection-details">
                <p><strong>Token Status:</strong> {connectionDetails.hasAccessToken ? '✅ Valid' : '❌ Missing'}</p>
                <p><strong>Refresh Token:</strong> {connectionDetails.hasRefreshToken ? '✅ Available' : '❌ Missing'}</p>
                {connectionDetails.tokenExpiry && (
                  <p><strong>Expires:</strong> {connectionDetails.tokenExpiry}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="test-section">
          <h3>🔬 Advanced Connection Testing</h3>
          <div className="advanced-tests">
            <button
              className="test-button secondary"
              onClick={testDetailedConnection}
              disabled={isTestingConnection}
            >
              {isTestingConnection ? 'Testing...' : '🔬 Detailed Connection Test'}
            </button>
            
            <button
              className="test-button secondary"
              onClick={forceTokenRefresh}
              disabled={!spotifyConnected}
            >
              🔄 Force Token Refresh
            </button>
            
            <button
              className="test-button warning"
              onClick={simulateExpiredToken}
              disabled={!spotifyConnected}
            >
              ⏰ Simulate Expired Token
            </button>
            
            <button
              className="test-button secondary"
              onClick={testTokenRefreshFlow}
              disabled={!spotifyConnected}
            >
              🔄 Test Token Refresh Flow
            </button>
          </div>
          <p className="form-help">
            These tests help verify token management and error handling scenarios.
          </p>
        </div>

        <div className="test-section">
          <h3>🎵 Playlist Creation Test</h3>
          <div className="playlist-form">
            <div className="form-group">
              <label>Playlist Name:</label>
              <input
                type="text"
                value={playlistName}
                onChange={(e) => setPlaylistName(e.target.value)}
                placeholder="Enter playlist name"
              />
            </div>
            <div className="form-group">
              <label>Description:</label>
              <input
                type="text"
                value={playlistDescription}
                onChange={(e) => setPlaylistDescription(e.target.value)}
                placeholder="Enter playlist description"
              />
            </div>
            <button
              className="test-button primary"
              onClick={handleCreatePlaylist}
              disabled={!spotifyConnected || isCreatingPlaylist}
            >
              {isCreatingPlaylist ? 'Creating...' : 'Create Test Playlist'}
            </button>
            <p className="form-help">
              This will create a playlist with 5 classic rock songs as a test.
            </p>
          </div>
        </div>

        <div className="test-section">
          <h3>📝 Test Logs</h3>
          <div className="log-controls">
            <button
              className="test-button secondary"
              onClick={clearLogs}
            >
              Clear Logs
            </button>
            <button
              className="test-button secondary"
              onClick={copyLogs}
            >
              📋 Copy Logs
            </button>
          </div>
          <div className="log-display">
            {testResults.length === 0 ? (
              <p className="no-logs">No logs yet. Start testing to see results.</p>
            ) : (
              testResults.map((log, index) => (
                <div key={index} className="log-entry">
                  {log}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="spotify-test-footer">
        <a href="/" className="back-link">← Back to Main App</a>
      </div>
    </div>
  );
};

export default SpotifyTest; 